import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { 
  Users, UserPlus, Trash2, X, Search, Shield, User, AlertCircle, Pencil
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ROLE_COLORS = {
  super_admin: 'bg-purple-100 text-purple-800 border-purple-300',
  admin: 'bg-blue-100 text-blue-800 border-blue-300',
  volunteer: 'bg-green-100 text-green-800 border-green-300'
};

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  volunteer: 'Volunteer'
};

// Booking-capacity role (separate from access role above)
const AVAILABILITY_ROLE_LABELS = {
  repairer: 'Repairer',
  front_of_house: 'Front of House',
  unavailable: 'Unavailable',
};

const AVAILABILITY_ROLE_COLORS = {
  repairer: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  front_of_house: 'bg-amber-100 text-amber-800 border-amber-300',
  unavailable: 'bg-gray-100 text-gray-700 border-gray-300',
};

export default function UserManagement() {
  const { getAuthHeader, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null); // { id, name, email, role }
  const [savingEdit, setSavingEdit] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [stats, setStats] = useState(null);

  // Create user form
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'volunteer',
    availability_role: 'repairer',
  });

  useEffect(() => {
    if (user?.role === 'super_admin' || user?.role === 'admin') {
      fetchUsers();
      if (user?.role === 'super_admin') {
        fetchStats();
      }
    }
  }, [user]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/users`, {
        headers: getAuthHeader()
      });
      console.log('✅ Users loaded:', response.data.length, 'users');
      setUsers(response.data);
    } catch (error) {
      console.error('❌ Error fetching users:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/users/stats`, {
        headers: getAuthHeader()
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    console.log('🔍 Form submitted with values:', {
      name: newUser.name,
      email: newUser.email,
      password: newUser.password ? '***' : '(empty)',
      role: newUser.role
    });
    
    if (!newUser.name || !newUser.email || !newUser.password) {
      console.warn('⚠️ Validation failed: Missing fields');
      toast.error('Please fill in all fields');
      return;
    }

    if (newUser.password.length < 8) {
      console.warn('⚠️ Validation failed: Password too short');
      toast.error('Password must be at least 8 characters');
      return;
    }

    try {
      console.log('📤 Sending request to API...');
      console.log('API URL:', `${API}/users`);
      console.log('Request data:', {
        name: newUser.name,
        email: newUser.email,
        role: newUser.role
      });
      
      const response = await axios.post(`${API}/users`, {
        name: newUser.name,
        email: newUser.email,
        password: newUser.password,
        role: newUser.role,
        availability_role: newUser.availability_role,
      }, {
        headers: getAuthHeader()
      });
      
      console.log('✅ SUCCESS! User created:', response.data);
      toast.success(`✅ User created: ${newUser.email}`);
      setShowCreateModal(false);
      setNewUser({ name: '', email: '', password: '', role: 'volunteer', availability_role: 'repairer' });
      await fetchUsers();
      if (user?.role === 'super_admin') {
        await fetchStats();
      }
    } catch (error) {
      console.error('❌ ERROR creating user');
      console.error('Full error object:', error);
      console.error('Response status:', error.response?.status);
      console.error('Response data:', error.response?.data);
      console.error('Response headers:', error.response?.headers);
      
      const errorDetail = error.response?.data?.detail;
      let errorMsg = 'Failed to create user';
      
      if (typeof errorDetail === 'string') {
        errorMsg = errorDetail;
      } else if (Array.isArray(errorDetail) && errorDetail.length > 0) {
        const firstErr = errorDetail[0];
        errorMsg = firstErr.msg || firstErr.message || errorDetail[0] || errorMsg;
      }
      
      console.error('📢 Showing error to user:', errorMsg);
      toast.error(errorMsg, { duration: 5000 });
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This cannot be undone.`)) {
      return;
    }

    try {
      console.log('🗑️ Deleting user:', userId, userName);
      
      await axios.delete(`${API}/users/${userId}`, {
        headers: getAuthHeader()
      });
      
      console.log('✅ User deleted successfully');
      toast.success(`User deleted: ${userName}`);
      await fetchUsers();
      if (user?.role === 'super_admin') {
        await fetchStats();
      }
    } catch (error) {
      console.error('❌ Delete user error:', error);
      console.error('Error details:', error.response?.data);
      
      const errorMsg = error.response?.data?.detail || 'Failed to delete user';
      toast.error(errorMsg);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;
    if (!editingUser.name.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    setSavingEdit(true);
    try {
      const payload = {
        name: editingUser.name.trim(),
        role: editingUser.role,
        availability_role: editingUser.availability_role || 'repairer',
      };
      await axios.patch(`${API}/users/${editingUser.id}`, payload, { headers: getAuthHeader() });
      toast.success(`Updated ${editingUser.name}`);
      setEditingUser(null);
      await fetchUsers();
      if (user?.role === 'super_admin') await fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSavingEdit(false);
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          u.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = !filterRole || u.role === filterRole;
    return matchesSearch && matchesRole;
  });

  // Both Super Admin and Admin can VIEW the users table. Admin sees a
  // read-only version: no Create / Edit / Delete buttons, no stats, but
  // every user row and Volunteer-Type badge is visible (Amendment 1).
  const canManageUsers = user?.role === 'super_admin';

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-[#193460] flex items-center gap-2">
          <Users className="w-8 h-8" />
          User Management
        </h1>
        <p className="text-gray-600 mt-1">Create and manage user accounts</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
            <div className="text-sm text-gray-600">Total Users</div>
            <div className="text-2xl font-bold text-gray-800">{stats.total_users}</div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg border-2 border-purple-200">
            <div className="text-sm text-purple-600">Super Admins</div>
            <div className="text-2xl font-bold text-purple-800">{stats.by_role.super_admin}</div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-200">
            <div className="text-sm text-blue-600">Admins</div>
            <div className="text-2xl font-bold text-blue-800">{stats.by_role.admin}</div>
          </div>
          <div className="bg-green-50 p-4 rounded-lg border-2 border-green-200">
            <div className="text-sm text-green-600">Volunteers</div>
            <div className="text-2xl font-bold text-green-800">{stats.by_role.volunteer}</div>
          </div>
        </div>
      )}

      {/* Actions Bar */}
      <div className="bg-white p-4 rounded-lg border-2 border-gray-200 mb-6 flex flex-col sm:flex-row sm:flex-wrap gap-3 items-stretch sm:items-center">
        {canManageUsers && (
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-[#E87B6A] hover:bg-[#d66a59] text-white w-full sm:w-auto"
            data-testid="create-user-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Create New User
          </Button>
        )}

        <div className="flex-1 w-full sm:min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <Select value={filterRole || "all"} onValueChange={(val) => setFilterRole(val === "all" ? "" : val)}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="All Roles" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="super_admin">Super Admin</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
            <SelectItem value="volunteer">Volunteer</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* User List */}
      <div className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full min-w-[760px]">
          <thead className="bg-gray-50 border-b-2 border-gray-200">
            <tr>
              <th className="text-left p-4 font-semibold text-gray-700">Name</th>
              <th className="text-left p-4 font-semibold text-gray-700">Email</th>
              <th className="text-left p-4 font-semibold text-gray-700">Role</th>
              <th className="text-left p-4 font-semibold text-gray-700">Booking Role</th>
              <th className="text-left p-4 font-semibold text-gray-700">Status</th>
              <th className="text-right p-4 font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="text-center p-8 text-gray-500">
                  Loading users...
                </td>
              </tr>
            ) : filteredUsers.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center p-8 text-gray-500">
                  No users found
                </td>
              </tr>
            ) : (
              filteredUsers.map((u) => (
                <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      {u.role === 'super_admin' ? (
                        <Shield className="w-4 h-4 text-purple-600" />
                      ) : (
                        <User className="w-4 h-4 text-gray-600" />
                      )}
                      <span className="font-medium text-gray-800">{u.name}</span>
                    </div>
                  </td>
                  <td className="p-4 text-gray-600">{u.email}</td>
                  <td className="p-4">
                    <Badge className={ROLE_COLORS[u.role]}>
                      {ROLE_LABELS[u.role]}
                    </Badge>
                  </td>
                  <td className="p-4">
                    <Badge
                      className={AVAILABILITY_ROLE_COLORS[u.availability_role || 'repairer']}
                      data-testid={`availability-badge-${u.id}`}
                    >
                      {AVAILABILITY_ROLE_LABELS[u.availability_role || 'repairer']}
                    </Badge>
                  </td>
                  <td className="p-4">
                    <Badge className={u.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                      {u.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {u.id !== user.id && user?.role === 'super_admin' && (
                        <Button
                          onClick={() => setEditingUser({
                            id: u.id,
                            name: u.name,
                            email: u.email,
                            role: u.role,
                            availability_role: u.availability_role || 'repairer',
                          })}
                          size="sm"
                          variant="outline"
                          className="border-[#193460] text-[#193460] hover:bg-[#193460] hover:text-white"
                          data-testid={`edit-user-${u.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                      )}
                      {u.id !== user.id && u.role !== 'super_admin' && user?.role === 'super_admin' && (
                        <Button
                          onClick={() => handleDeleteUser(u.id, u.name)}
                          variant="destructive"
                          size="sm"
                          className="bg-red-600 hover:bg-red-700"
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                      {/* Admins see read-only badges; only Super Admins can edit. */}
                      {user?.role === 'admin' && (
                        <span className="text-xs text-gray-400 italic">View only</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-[#193460]">Create New User</h2>
              <button onClick={() => setShowCreateModal(false)}>
                <X className="w-6 h-6 text-gray-600 hover:text-gray-800" />
              </button>
            </div>

            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={newUser.name}
                  onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                  placeholder="John Doe"
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  placeholder="john@example.com"
                  required
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Must be a valid email address
                </p>
              </div>

              <div>
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  placeholder="Minimum 8 characters"
                  required
                  minLength={8}
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Must be at least 8 characters long
                </p>
              </div>

              <div>
                <Label htmlFor="role">Role *</Label>
                <Select value={newUser.role} onValueChange={(val) => setNewUser({...newUser, role: val})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Super Admin can create admin or volunteer */}
                    {user?.role === 'super_admin' && (
                      <SelectItem value="admin">Admin</SelectItem>
                    )}
                    {/* Both admin and super_admin can create volunteers */}
                    <SelectItem value="volunteer">Volunteer</SelectItem>
                  </SelectContent>
                </Select>
                {user?.role === 'admin' && (
                  <p className="text-xs text-gray-500 mt-1">Admins can only create volunteer accounts</p>
                )}
              </div>

              <div>
                <Label htmlFor="availability_role">Booking Availability *</Label>
                <Select
                  value={newUser.availability_role}
                  onValueChange={(val) => setNewUser({ ...newUser, availability_role: val })}
                  disabled={user?.role !== 'super_admin'}
                >
                  <SelectTrigger className="mt-1" data-testid="create-availability-role">
                    <SelectValue placeholder="Select booking role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="repairer">Repairer (counts towards booking capacity)</SelectItem>
                    <SelectItem value="front_of_house">Front of House (does not take bookings)</SelectItem>
                    <SelectItem value="unavailable">Unavailable (excluded from capacity)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  {user?.role === 'super_admin'
                    ? <>Only <strong>Repairers</strong> are counted by the booking system. Front of House volunteers help on the day but do not reserve a repair slot.</>
                    : 'New users default to Repairer. Only a Super Admin can change this.'}
                </p>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewUser({ name: '', email: '', password: '', role: 'volunteer', availability_role: 'repairer' });
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#E87B6A] hover:bg-[#d66a59] text-white"
                >
                  Create User
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="edit-user-modal">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-[#193460]">Edit User</h2>
              <Button onClick={() => setEditingUser(null)} variant="ghost" size="sm">
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="space-y-4">
              <div>
                <Label>Email (read-only)</Label>
                <Input value={editingUser.email} disabled className="mt-1 bg-gray-50" />
              </div>
              <div>
                <Label htmlFor="edit-name">Full Name *</Label>
                <Input id="edit-name" data-testid="edit-user-name"
                  value={editingUser.name}
                  onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                  className="mt-1" />
              </div>
              <div>
                <Label htmlFor="edit-role">Role *</Label>
                <Select value={editingUser.role}
                  onValueChange={(val) => setEditingUser({ ...editingUser, role: val })}>
                  <SelectTrigger className="mt-1" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="volunteer">Volunteer</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Super Admins can manage users, categories, event settings, schedule, and inline content.
                </p>
              </div>
              <div>
                <Label htmlFor="edit-availability-role">Booking Availability *</Label>
                <Select
                  value={editingUser.availability_role || 'repairer'}
                  onValueChange={(val) => setEditingUser({ ...editingUser, availability_role: val })}
                >
                  <SelectTrigger className="mt-1" data-testid="edit-availability-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="repairer">Repairer (counts towards booking capacity)</SelectItem>
                    <SelectItem value="front_of_house">Front of House (does not take bookings)</SelectItem>
                    <SelectItem value="unavailable">Unavailable (excluded from capacity)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Toggle this when a volunteer moves between repairing and front-of-house duties.
                </p>
              </div>
              <div className="flex gap-3 pt-4 border-t">
                <Button type="button" onClick={() => setEditingUser(null)} variant="outline" className="flex-1">
                  Cancel
                </Button>
                <Button type="button" onClick={handleSaveEdit} disabled={savingEdit}
                  data-testid="save-edit-user"
                  className="flex-1 bg-[#E87B6A] hover:bg-[#d66a59] text-white">
                  {savingEdit ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
